import React, { useState, useEffect, useCallback } from 'react';
import { leaguesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Settings2, Plus, Minus, Trash2, Save, Trophy, TrendingUp, TrendingDown, Edit3, RotateCcw, AlertTriangle
} from 'lucide-react';
import { getInitials } from '@/lib/utils';

// ── Scoring System Settings ──
export const ScoringSettings = ({ leagueId }) => {
  const [scoring, setScoring] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await leaguesAPI.getScoring(leagueId);
        setScoring(res.data.scoring_system);
      } catch (err) {
        setScoring({ points_per_win: 3, points_per_draw: 1, points_per_loss: 1 });
      } finally {
        setLoading(false);
      }
    })();
  }, [leagueId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await leaguesAPI.updateScoring(leagueId, scoring);
      toast.success('Sistema di punteggio aggiornato!');
    } catch (err) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !scoring) return <Skeleton className="h-32" />;

  const fields = [
    { key: 'points_per_win', label: 'Punti per Vittoria', icon: <Trophy className="h-4 w-4 text-green-500" /> },
    { key: 'points_per_draw', label: 'Punti per Pareggio', icon: <Minus className="h-4 w-4 text-yellow-500" /> },
    { key: 'points_per_loss', label: 'Punti per Sconfitta (presenza)', icon: <TrendingDown className="h-4 w-4 text-red-500" /> },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Settings2 className="h-5 w-5" />
          Sistema di Punteggio
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          {fields.map(({ key, label, icon }) => (
            <div key={key} className="space-y-1">
              <Label className="text-xs flex items-center gap-1.5">{icon} {label}</Label>
              <Input
                type="number"
                value={scoring[key]}
                onChange={(e) => setScoring(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                className="h-9"
              />
            </div>
          ))}
        </div>
        <div className="space-y-2 pt-1">
          <Label className="text-xs flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Penalità per chi non paga la quota del campo entro 30 minuti dall'inizio
          </Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Punti in meno</Label>
              <Input
                type="number"
                min="0"
                value={scoring.late_payment_points ?? 0}
                onChange={(e) => setScoring(prev => ({ ...prev, late_payment_points: Math.abs(parseInt(e.target.value) || 0) }))}
                className="h-9"
                data-testid="late-points-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Euro in più sulla quota</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={scoring.late_payment_euro ?? 0}
                onChange={(e) => setScoring(prev => ({ ...prev, late_payment_euro: Math.abs(parseFloat(e.target.value) || 0) }))}
                className="h-9"
                data-testid="late-euro-input"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Vale per le partite con la raccolta del campo in app. 0 = nessuna penalità.</p>
        </div>
        <p className="text-xs text-muted-foreground">
          I punti importati restano invariati. Queste impostazioni si applicano solo alle partite giocate tramite l'app.
        </p>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Punteggio'}
        </Button>
      </CardContent>
    </Card>
  );
};

// ── Point Adjustments (Bonus / Penalties) – Grouped by User with detail rows ──
export const PointAdjustments = ({ leagueId, members }) => {
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ user_id: '', points: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchAdjustments = useCallback(async () => {
    try {
      const res = await leaguesAPI.getPointAdjustments(leagueId);
      setGrouped(res.data.grouped || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => { fetchAdjustments(); }, [fetchAdjustments]);

  const handleAdd = async () => {
    if (!form.user_id || !form.points) {
      toast.error('Seleziona un membro e inserisci i punti');
      return;
    }
    setSubmitting(true);
    try {
      await leaguesAPI.addPointAdjustment(leagueId, {
        user_id: form.user_id,
        points: parseInt(form.points),
        reason: form.reason,
      });
      toast.success('Correzione aggiunta!');
      setShowDialog(false);
      setForm({ user_id: '', points: '', reason: '' });
      fetchAdjustments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (adjustmentId) => {
    if (!window.confirm('Eliminare questa correzione?')) return;
    try {
      await leaguesAPI.deletePointAdjustment(leagueId, adjustmentId);
      toast.success('Correzione eliminata');
      fetchAdjustments();
    } catch (err) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Eliminare tutte le correzioni per questo utente?')) return;
    try {
      await leaguesAPI.deleteUserAdjustments(leagueId, userId);
      setGrouped(prev => prev.filter(g => g.user_id !== userId));
      toast.success('Correzioni eliminate');
    } catch (err) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  if (loading) return <Skeleton className="h-32" />;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="h-5 w-5" />
          Correzioni Punteggio
        </CardTitle>
        <Button size="sm" onClick={() => { setForm({ user_id: '', points: '', reason: '' }); setShowDialog(true); }}>
          <Plus className="h-4 w-4 mr-1" /> Aggiungi
        </Button>
      </CardHeader>
      <CardContent>
        {grouped.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Nessuna correzione attiva. Usa questa sezione per aggiungere bonus o penalità.
          </p>
        ) : (
          <div className="space-y-3">
            {grouped.map(item => (
              <div key={item.user_id} className="rounded-lg border overflow-hidden">
                {/* User header */}
                <div className="flex items-center justify-between p-2 bg-muted/50">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={item.user?.picture} />
                      <AvatarFallback className="text-[10px]">{getInitials(item.user?.name)}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{item.user?.name}</span>
                    {item.user?.nickname && (
                      <span className="text-xs text-primary italic">&laquo;{item.user.nickname}&raquo;</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={item.total_points > 0 ? "default" : "destructive"}>
                      Totale: {item.total_points > 0 ? `+${item.total_points}` : item.total_points}
                    </Badge>
                    {item.entries?.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteUser(item.user_id)} title="Elimina tutte">
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* Individual correction rows */}
                <div className="divide-y">
                  {item.entries?.map(entry => {
                    const dateStr = entry.created_at 
                      ? new Date(entry.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
                      : '';
                    return (
                      <div key={entry.adjustment_id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-muted-foreground truncate">
                            {entry.reason || 'Correzione'}
                          </span>
                          {dateStr && (
                            <span className="text-xs text-muted-foreground/60 shrink-0">
                              {dateStr}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant={entry.points > 0 ? "outline" : "destructive"} className="text-xs">
                            {entry.points > 0 ? `+${entry.points}` : entry.points}
                          </Badge>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDeleteEntry(entry.adjustment_id)}>
                            <Trash2 className="h-3 w-3 text-destructive/70" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Correzione Punteggio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Membro</Label>
              <select
                value={form.user_id}
                onChange={(e) => setForm(prev => ({ ...prev, user_id: e.target.value }))}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Seleziona un membro...</option>
                {members.map(m => (
                  <option key={m.user_id} value={m.user_id}>{m.user?.name || m.user_id}{m.user?.nickname ? ` «${m.user.nickname}»` : ''}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Punti (positivi = bonus, negativi = penalità)</Label>
              <Input
                type="number"
                value={form.points}
                onChange={(e) => setForm(prev => ({ ...prev, points: e.target.value }))}
                placeholder="Es: +2 o -3"
              />
            </div>
            <div className="space-y-2">
              <Label>Motivo (opzionale)</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Es: Bonus fair play, Penalità assenza..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleAdd} disabled={submitting}>
              {submitting ? 'Salvataggio...' : 'Aggiungi'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ── Member Stats Editor ──
export const MemberStatsEditor = ({ leagueId, members, onUpdate }) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = (member) => {
    const stats = member.stats || {};
    setEditingId(member.user_id);
    setEditForm({
      matches_played: stats.matches_played || 0,
      wins: stats.wins || 0,
      draws: stats.draws || 0,
      losses: stats.losses || 0,
      total_points: stats.total_points || 0,
      goals_scored: stats.goals_scored || 0,
      goals_conceded: stats.goals_conceded || 0,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await leaguesAPI.updateMemberStats(leagueId, editingId, editForm);
      toast.success('Statistiche aggiornate!');
      setEditingId(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const statFields = [
    { key: 'matches_played', label: 'Partite', short: 'PG' },
    { key: 'wins', label: 'Vittorie', short: 'V' },
    { key: 'draws', label: 'Pareggi', short: 'P' },
    { key: 'losses', label: 'Sconfitte', short: 'S' },
    { key: 'goals_scored', label: 'Gol Fatti', short: 'GF' },
    { key: 'goals_conceded', label: 'Gol Subiti', short: 'GS' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Edit3 className="h-5 w-5" />
          Modifica Statistiche Membri
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {members.map(member => {
            const stats = member.stats || {};
            const isEditing = editingId === member.user_id;

            return (
              <div key={member.user_id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={member.user?.picture} />
                      <AvatarFallback className="text-[10px]">{getInitials(member.user?.name)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-sm">{member.user?.name}</span>
                    {member.user?.nickname && (
                      <span className="text-xs text-primary italic">&laquo;{member.user.nickname}&raquo;</span>
                    )}
                  </div>
                  {!isEditing ? (
                    <Button variant="ghost" size="sm" onClick={() => startEdit(member)}>
                      <Edit3 className="h-3 w-3 mr-1" /> Modifica
                    </Button>
                  ) : (
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => setEditingId(null)}>Annulla</Button>
                      <Button size="sm" onClick={handleSave} disabled={saving}>
                        <Save className="h-3 w-3 mr-1" /> {saving ? '...' : 'Salva'}
                      </Button>
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div className="grid grid-cols-3 gap-2">
                    {statFields.map(({ key, label, short }) => (
                      <div key={key} className="space-y-0.5">
                        <Label className="text-[10px] text-muted-foreground">{label}</Label>
                        <Input
                          type="number"
                          value={editForm[key]}
                          onChange={(e) => setEditForm(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                          className="h-8 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-3 text-xs text-muted-foreground flex-wrap">
                    <span>PG: <b>{stats.matches_played || 0}</b></span>
                    <span className="text-green-600">V: <b>{stats.wins || 0}</b></span>
                    <span className="text-yellow-600">P: <b>{stats.draws || 0}</b></span>
                    <span className="text-red-600">S: <b>{stats.losses || 0}</b></span>
                    <span>GF: <b>{stats.goals_scored || 0}</b></span>
                    <span>GS: <b>{stats.goals_conceded || 0}</b></span>
                    <span>Punti: <b>{stats.total_points || 0}</b></span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Season Reset ──
export const SeasonReset = ({ leagueId, leagueName, seasonStartedAt }) => {
  const [resetting, setResetting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [seasonName, setSeasonName] = useState('');

  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await leaguesAPI.resetSeason(leagueId, {
        season_name: seasonName.trim(),
        expected_started_at: seasonStartedAt,
      });
      toast.success(res.data.message || 'Stagione azzerata!');
      setConfirmText('');
      setSeasonName('');
      // Reload the page to reflect changes
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel reset della stagione');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-destructive">
          <RotateCcw className="h-5 w-5" />
          Reset Stagione
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-destructive">Attenzione: questa azione è irreversibile!</p>
              <p className="text-muted-foreground mt-1">
                La classifica finale e le partite verranno salvate nello storico. Poi saranno azzerate
                le statistiche e rimossi i bonus/penalità. I membri della lega non verranno rimossi.
              </p>
            </div>
          </div>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm" className="w-full">
              <RotateCcw className="h-4 w-4 mr-2" />
              Azzera Stagione
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Reset Stagione - {leagueName}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>Questa operazione resetterà:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Tutte le statistiche dei giocatori (punti, vittorie, pareggi, sconfitte, gol)</li>
                    <li>Tutte le correzioni di punteggio (bonus/penalità)</li>
                    <li>Classifica finale e partite completate verranno archiviate e resteranno consultabili</li>
                  </ul>
                  <div className="space-y-1.5">
                    <Label htmlFor="season-name">Nome stagione (facoltativo)</Label>
                    <Input
                      id="season-name"
                      value={seasonName}
                      onChange={(e) => setSeasonName(e.target.value)}
                      maxLength={80}
                      placeholder="Es. Stagione 2025/26"
                    />
                  </div>
                  <Label htmlFor="season-reset-confirm" className="font-medium">
                    Scrivi <span className="text-destructive font-bold">RESET</span> per confermare:
                  </Label>
                  <Input
                    id="season-reset-confirm"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Scrivi RESET per confermare"
                    className="border-destructive/50"
                  />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => { setConfirmText(''); setSeasonName(''); }}>Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleReset}
                disabled={confirmText !== 'RESET' || resetting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {resetting ? 'Azzeramento...' : 'Conferma Reset'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
